import { generateCodeChallenge, generateCodeVerifier } from "@/utils/pkce";
import { mockSsoService } from "@/services/mock-sso-service";
import { toast } from "@/hooks/use-toast";

// 개발 서버에서만 (운영 빌드에는 .env.local의 모의 SSO 값이 들어가지 않게)
const IS_MOCK_SSO = import.meta.env.DEV && import.meta.env.VITE_MOCK_SSO === "true";
const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID || "";
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID || "";
const REDIRECT_URI = import.meta.env.VITE_ENTRA_REDIRECT_URI || "";
const LOGOUT_REDIRECT_URI = import.meta.env.VITE_ENTRA_POST_LOGOUT_REDIRECT_URI || "";

const MSG_MISSING_CONFIG = "SSO 설정이 완료되지 않았습니다. 관리자에게 문의해 주세요.";

export const ssoService = {
  async getLoginUrl() {
    if (IS_MOCK_SSO) return mockSsoService.getLoginUrl();

    if (!TENANT_ID || !CLIENT_ID) {
      toast({
        variant: "destructive",
        title: "SSO 설정 오류",
        description: MSG_MISSING_CONFIG,
      });
      throw new Error(MSG_MISSING_CONFIG);
    }

    const verifier = generateCodeVerifier();
    localStorage.setItem("pkce_code_verifier", verifier);
    const challenge = await generateCodeChallenge(verifier);

    const baseUrl = `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/authorize`;
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      response_type: "code",
      redirect_uri: REDIRECT_URI,
      scope: "openid profile email",
      // prompt: "select_account",
      code_challenge: challenge,
      code_challenge_method: "S256",
      prompt: "select_account",
    });
    return `${baseUrl}?${params.toString()}`;
  },

  async exchangeToken(code: string) {
    if (IS_MOCK_SSO) return mockSsoService.exchangeToken(code);

    const verifier = localStorage.getItem("pkce_code_verifier");
    if (!verifier) {
      const hasToken = !!localStorage.getItem("access_token");
      if (hasToken) {
        return { access_token: "", refresh_token: "" };
      }
      throw new Error("No PKCE code verifier found in local storage");
    }
    localStorage.removeItem("pkce_code_verifier");

    const tokenUrl = `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/token`;

    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      grant_type: "authorization_code",
      redirect_uri: REDIRECT_URI,
      code,
      code_verifier: verifier,
    });

    const response = await fetch(tokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error_description || "Failed to exchange token");
    }

    const data = await response.json();
    return {
      access_token: data.id_token,
      refresh_token: data.refresh_token,
    };
  },

  getLogoutUrl() {
    if (IS_MOCK_SSO) return mockSsoService.getLogoutUrl();

    return `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(LOGOUT_REDIRECT_URI || window.location.origin)}`;
  },
};
